// The dashboard's date ranges, picked the way Shopify's admin picks them. A
// range runs from its start up to, not including, its end, in the browser's
// time zone. Weeks start on Monday, as the API's weekly periods do.
//
// Kept in step with packages/syncing-app/app/lib/date-range.ts.

export type LastUnit =
  | "minute"
  | "hour"
  | "day"
  | "week"
  | "month"
  | "quarter"
  | "year";
export type PeriodUnit = "week" | "month" | "quarter" | "year";
export type Quarter = 1 | 2 | 3 | 4;

export type DateRangeOption =
  | { kind: "today" }
  | { kind: "yesterday" }
  // minutes and hours run to now, days include today, longer units are the
  // complete ones before the current one, e.g. last month
  | { kind: "last"; amount: number; unit: LastUnit }
  | { kind: "toDate"; unit: PeriodUnit }
  | { kind: "quarter"; year: number; quarter: Quarter }
  | { kind: "custom"; start: Date; end: Date };

export type DateRange = { start: Date; end: Date };

export const LAST_UNITS: LastUnit[] = [
  "minute",
  "hour",
  "day",
  "week",
  "month",
  "quarter",
  "year",
];

export const DEFAULT_RANGE_OPTION: DateRangeOption = {
  kind: "last",
  amount: 30,
  unit: "day",
};

const last = (amount: number, unit: LastUnit): DateRangeOption => ({
  kind: "last",
  amount,
  unit,
});
const toDate = (unit: PeriodUnit): DateRangeOption => ({ kind: "toDate", unit });

// the picker's list, grouped as Shopify groups them
export function presetGroups(now = new Date()): DateRangeOption[][] {
  const year = now.getFullYear();
  return [
    [{ kind: "today" }, { kind: "yesterday" }],
    [
      last(30, "minute"),
      last(12, "hour"),
      last(7, "day"),
      last(30, "day"),
      last(90, "day"),
      last(365, "day"),
      last(1, "week"),
      last(1, "month"),
      last(1, "quarter"),
      last(12, "month"),
      last(1, "year"),
    ],
    [toDate("week"), toDate("month"), toDate("quarter"), toDate("year")],
    ([1, 2, 3, 4] as const).map((quarter) => ({
      kind: "quarter",
      year,
      quarter,
    })),
  ];
}

export function sameOption(a: DateRangeOption, b: DateRangeOption): boolean {
  switch (a.kind) {
    case "last":
      return b.kind === "last" && a.amount === b.amount && a.unit === b.unit;
    case "toDate":
      return b.kind === "toDate" && a.unit === b.unit;
    case "quarter":
      return b.kind === "quarter" && a.year === b.year && a.quarter === b.quarter;
    case "custom":
      return (
        b.kind === "custom" &&
        a.start.getTime() === b.start.getTime() &&
        a.end.getTime() === b.end.getTime()
      );
    default:
      return a.kind === b.kind;
  }
}

// a quarter that hasn't started can't be picked
export function quarterStarted(year: number, quarter: Quarter, now = new Date()) {
  return new Date(year, (quarter - 1) * 3, 1) <= now;
}

const MINUTE = 60_000;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function startOf(date: Date, unit: PeriodUnit): Date {
  switch (unit) {
    case "week":
      return addDays(startOfDay(date), -((date.getDay() + 6) % 7));
    case "month":
      return new Date(date.getFullYear(), date.getMonth(), 1);
    case "quarter":
      return new Date(date.getFullYear(), date.getMonth() - (date.getMonth() % 3), 1);
    case "year":
      return new Date(date.getFullYear(), 0, 1);
  }
}

function addUnits(date: Date, unit: PeriodUnit, amount: number): Date {
  switch (unit) {
    case "week":
      return addDays(date, amount * 7);
    case "month":
      return new Date(date.getFullYear(), date.getMonth() + amount, 1);
    case "quarter":
      return new Date(date.getFullYear(), date.getMonth() + amount * 3, 1);
    case "year":
      return new Date(date.getFullYear() + amount, 0, 1);
  }
}

/**
 * The range an option covers at `at`. Ranges that run to now end at the next
 * whole minute, so they stay the same, and so does the query for them, for
 * the rest of the minute; the API counts a range ending later up to now.
 */
export function resolveRange(option: DateRangeOption, at = new Date()): DateRange {
  const now = new Date(Math.ceil(at.getTime() / MINUTE) * MINUTE);
  const today = startOfDay(at);

  switch (option.kind) {
    case "today":
      return { start: today, end: addDays(today, 1) };
    case "yesterday":
      return { start: addDays(today, -1), end: today };
    case "last": {
      const amount = Math.max(1, Math.floor(option.amount));
      switch (option.unit) {
        case "minute":
          return { start: new Date(now.getTime() - amount * MINUTE), end: now };
        case "hour":
          return { start: new Date(now.getTime() - amount * 60 * MINUTE), end: now };
        case "day":
          return { start: addDays(today, 1 - amount), end: now };
        default: {
          const end = startOf(at, option.unit);
          return { start: addUnits(end, option.unit, -amount), end };
        }
      }
    }
    case "toDate":
      return { start: startOf(at, option.unit), end: now };
    case "quarter": {
      const start = new Date(option.year, (option.quarter - 1) * 3, 1);
      return { start, end: addUnits(start, "quarter", 1) };
    }
    case "custom":
      return { start: option.start, end: option.end };
  }
}

// the date and time inputs show and take the last minute a range includes
export function inclusiveEnd(end: Date): Date {
  return new Date(end.getTime() - MINUTE);
}

export function exclusiveEnd(lastMinute: Date): Date {
  return new Date(lastMinute.getTime() + MINUTE);
}

const pad = (n: number) => String(n).padStart(2, "0");

// yyyy-mm-dd for a date input
export function toDateInput(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// hh:mm for a time input
export function toTimeInput(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// a local date and time from the inputs, null when either isn't valid
export function fromInputs(date: string, time: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time || "00:00");
  if (!d || !t) return null;
  const result = new Date(+d[1], +d[2] - 1, +d[3], +t[1], +t[2]);
  return Number.isNaN(result.getTime()) ? null : result;
}

export function timeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}
